/**
 * Kintlévőség — nyitott + lejárt számlák, aging, fizetés rögzítés, felszólítás
 */
import { useState, useEffect } from 'react';
import { get, post } from '../../lib/api';
import { PageHeader, Button, Badge, Card, Input, Modal, EmptyState } from '../../components/ui';
import { useToast } from '../../components/ui/Toast';
import MoneyInput from '../../components/ui/MoneyInput';
import ActionsDropdown from '../../components/ActionsDropdown';

interface OpenInvoice {
  id: string; invoice_number: string; partner_name?: string; partner_id?: string;
  due_date: string; issue_date: string; subtotal_gross: number; paid_amount: number;
  status: string; type: string; payment_method: string;
}

function fmt(n: number) { return Math.round(n).toLocaleString('hu-HU') + ' Ft'; }
function fmtDate(iso: string | undefined) { if (!iso) return '—'; try { return new Date(iso).toLocaleDateString('hu-HU', { month: 'short', day: 'numeric' }); } catch { return '—'; } }

function daysOverdue(dueDate: string): number {
  const now = new Date(); now.setHours(0, 0, 0, 0);
  const d = new Date(dueDate); d.setHours(0, 0, 0, 0);
  return Math.floor((now.getTime() - d.getTime()) / (1000 * 60 * 60 * 24));
}

function agingLabel(days: number): string {
  if (days <= 0) return 'Nem lejárt';
  if (days <= 30) return '1-30 nap';
  if (days <= 60) return '31-60 nap';
  if (days <= 90) return '61-90 nap';
  return '90+ nap';
}

function agingColor(days: number): string {
  if (days <= 0) return '#16a34a';
  if (days <= 30) return '#d97706';
  if (days <= 60) return '#ea580c';
  if (days <= 90) return '#dc2626';
  return '#991b1b';
}

export default function ReceivablesPage() {
  const { toast } = useToast();
  const [invoices, setInvoices] = useState<OpenInvoice[]>([]);
  const [loading, setLoading] = useState(true);
  const [showPayment, setShowPayment] = useState<OpenInvoice | null>(null);
  const [filter, setFilter] = useState('all'); // all | overdue | 30 | 60 | 90

  async function load() {
    setLoading(true);
    try {
      // Nyitott számlák: issued, normal, nem teljesen fizetve
      const d = await get<any>('/invoices?status=issued&type=normal&pageSize=200');
      const list: OpenInvoice[] = (Array.isArray(d) ? d : d?.data || [])
        .filter((inv: any) => {
          const gross = Number(inv.subtotal_gross || 0);
          const paid = Number(inv.paid_amount || 0);
          return gross > 0 && paid < gross;
        })
        .map((inv: any) => ({
          ...inv,
          subtotal_gross: Number(inv.subtotal_gross || 0),
          paid_amount: Number(inv.paid_amount || 0),
        }));
      setInvoices(list);
    } catch { /* */ }
    setLoading(false);
  }

  useEffect(() => { load(); }, []);

  // Aging számítás
  const buckets = [
    { key: 'not_due', label: 'Nem lejárt', invoices: invoices.filter(i => daysOverdue(i.due_date) <= 0), color: '#16a34a' },
    { key: '1-30', label: '1-30 nap', invoices: invoices.filter(i => { const d = daysOverdue(i.due_date); return d > 0 && d <= 30; }), color: '#d97706' },
    { key: '31-60', label: '31-60 nap', invoices: invoices.filter(i => { const d = daysOverdue(i.due_date); return d > 30 && d <= 60; }), color: '#ea580c' },
    { key: '61-90', label: '61-90 nap', invoices: invoices.filter(i => { const d = daysOverdue(i.due_date); return d > 60 && d <= 90; }), color: '#dc2626' },
    { key: '90+', label: '90+ nap', invoices: invoices.filter(i => daysOverdue(i.due_date) > 90), color: '#991b1b' },
  ];

  const totalOpen = invoices.reduce((s, i) => s + (i.subtotal_gross - i.paid_amount), 0);
  const totalOverdue = invoices.filter(i => daysOverdue(i.due_date) > 0).reduce((s, i) => s + (i.subtotal_gross - i.paid_amount), 0);
  const overdueCount = invoices.filter(i => daysOverdue(i.due_date) > 0).length;

  // Szűrt lista
  const filtered = filter === 'all' ? invoices
    : filter === 'overdue' ? invoices.filter(i => daysOverdue(i.due_date) > 0)
    : invoices.filter(i => { const d = daysOverdue(i.due_date); return filter === '30' ? d > 0 && d <= 30 : filter === '60' ? d > 30 && d <= 60 : filter === '90' ? d > 60 && d <= 90 : d > 90; });

  // Rendezés: legtöbb napja lejárt felül
  const sorted = [...filtered].sort((a, b) => daysOverdue(b.due_date) - daysOverdue(a.due_date));

  return (
    <div>
      <PageHeader title="Kintlévőség" description={`${invoices.length} nyitott számla`} />

      {/* KPI kártyák */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: '12px', marginBottom: '20px' }}>
        <div style={{ padding: '14px 16px', borderRadius: 'var(--r)', background: 'var(--white)', border: '1px solid var(--line)', borderLeft: '3px solid #2563eb', cursor: 'pointer' }} onClick={() => setFilter('all')}>
          <div style={{ fontSize: '10px', textTransform: 'uppercase', letterSpacing: '1px', color: 'var(--slate)', fontWeight: 700 }}>Összes nyitott</div>
          <div style={{ fontSize: '20px', fontWeight: 800, color: 'var(--navy)', marginTop: '4px' }}>{fmt(totalOpen)}</div>
          <div style={{ fontSize: '11px', color: 'var(--slate)' }}>{invoices.length} számla</div>
        </div>
        <div style={{ padding: '14px 16px', borderRadius: 'var(--r)', background: 'var(--white)', border: '1px solid var(--line)', borderLeft: `3px solid ${overdueCount > 0 ? '#dc2626' : '#16a34a'}`, cursor: 'pointer' }} onClick={() => setFilter('overdue')}>
          <div style={{ fontSize: '10px', textTransform: 'uppercase', letterSpacing: '1px', color: 'var(--slate)', fontWeight: 700 }}>Lejárt összesen</div>
          <div style={{ fontSize: '20px', fontWeight: 800, color: overdueCount > 0 ? '#dc2626' : '#16a34a', marginTop: '4px' }}>{fmt(totalOverdue)}</div>
          <div style={{ fontSize: '11px', color: 'var(--slate)' }}>{overdueCount} számla</div>
        </div>
      </div>

      {/* Aging sáv */}
      <Card style={{ padding: '16px', marginBottom: '16px' }}>
        <div style={{ fontSize: '12px', fontWeight: 700, color: 'var(--navy)', marginBottom: '10px' }}>Aging bontás</div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(100px, 1fr))', gap: '8px' }}>
          {buckets.map(b => {
            const amount = b.invoices.reduce((s, i) => s + (i.subtotal_gross - i.paid_amount), 0);
            return (
              <button key={b.key} onClick={() => setFilter(b.key === 'not_due' ? 'all' : b.key.replace('-', '').replace('+', ''))}
                style={{
                  padding: '10px', borderRadius: 'var(--r)', border: '1px solid var(--line)',
                  background: 'var(--white)', cursor: 'pointer', textAlign: 'center',
                  borderBottom: `3px solid ${b.color}`,
                }}>
                <div style={{ fontSize: '10px', color: 'var(--slate)', fontWeight: 600 }}>{b.label}</div>
                <div style={{ fontSize: '14px', fontWeight: 800, color: b.color, marginTop: '2px' }}>{fmt(amount)}</div>
                <div style={{ fontSize: '10px', color: 'var(--slate)' }}>{b.invoices.length} db</div>
              </button>
            );
          })}
        </div>
      </Card>

      {/* Szűrő gombok */}
      <div style={{ display: 'flex', gap: '4px', marginBottom: '14px', flexWrap: 'wrap' }}>
        {[
          { key: 'all', label: `Mind (${invoices.length})` },
          { key: 'overdue', label: `Lejárt (${overdueCount})` },
        ].map(f => (
          <button key={f.key} onClick={() => setFilter(f.key)}
            style={{
              padding: '4px 10px', fontSize: '12px', border: '1px solid var(--line)', borderRadius: 'var(--r)',
              background: filter === f.key ? 'var(--navy)' : 'var(--white)',
              color: filter === f.key ? '#fff' : 'var(--ink)',
              cursor: 'pointer', fontWeight: filter === f.key ? 600 : 400,
            }}>{f.label}</button>
        ))}
      </div>

      {/* Lista */}
      {loading ? (
        <div style={{ padding: '40px', textAlign: 'center', color: 'var(--slate)' }}>Betöltés...</div>
      ) : sorted.length === 0 ? (
        <EmptyState icon="🎉" title={filter === 'all' ? 'Nincs nyitott számla' : 'Nincs lejárt számla'} description="Minden ki van egyenlítve!" />
      ) : (
        <div style={{ display: 'grid', gap: '8px' }}>
          {sorted.map(inv => {
            const days = daysOverdue(inv.due_date);
            const remaining = inv.subtotal_gross - inv.paid_amount;
            const isOverdue = days > 0;
            const color = agingColor(days);

            return (
              <Card key={inv.id} style={{ padding: '12px 16px', borderLeft: `3px solid ${color}` }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '8px' }}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap', marginBottom: '4px' }}>
                      <span style={{ fontWeight: 700, fontSize: '14px', color: 'var(--navy)', textTransform: 'uppercase', letterSpacing: '0.3px' }}>
                        {inv.invoice_number}
                      </span>
                      {isOverdue && (
                        <Badge variant="error" style={{ fontSize: '10px' }}>{days} napja lejárt</Badge>
                      )}
                      {!isOverdue && (
                        <Badge variant="default" style={{ fontSize: '10px' }}>{Math.abs(days)} nap van hátra</Badge>
                      )}
                    </div>
                    <div style={{ fontSize: '13px', fontWeight: 600, color: 'var(--ink)' }}>{inv.partner_name || '—'}</div>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '12px', fontSize: '12px', color: 'var(--slate)', marginTop: '4px' }}>
                      <span>Kelt: {fmtDate(inv.issue_date)}</span>
                      <span style={{ color: isOverdue ? color : undefined, fontWeight: isOverdue ? 600 : undefined }}>
                        Esedékes: {fmtDate(inv.due_date)}
                      </span>
                    </div>
                    {inv.paid_amount > 0 && (
                      <div style={{ fontSize: '11px', color: '#16a34a', marginTop: '2px' }}>
                        Fizetve: {fmt(inv.paid_amount)} / {fmt(inv.subtotal_gross)}
                      </div>
                    )}
                  </div>
                  <div style={{ textAlign: 'right', flexShrink: 0 }}>
                    <div style={{ fontWeight: 800, fontSize: '16px', color }}>
                      {fmt(remaining)}
                    </div>
                    <div style={{ fontSize: '10px', color: 'var(--slate)' }}>hátralék</div>
                  </div>
                  <div onClick={e => e.stopPropagation()}>
                    <ActionsDropdown actions={[
                      { label: 'Fizetés rögzítése', icon: '💰', onClick: () => setShowPayment(inv) },
                      { label: 'Felszólítás küldése', icon: '✉️', onClick: async () => {
                        try {
                          await post('/payment-reminders', { invoiceId: inv.id, level: days > 60 ? 3 : days > 30 ? 2 : 1 });
                          toast('Felszólítás elküldve.', 'success');
                        } catch (e: any) { toast(e.message || 'Hiba.', 'error'); }
                      }},
                    ]} />
                  </div>
                </div>
              </Card>
            );
          })}
        </div>
      )}

      {/* Fizetés modal */}
      {showPayment && <PaymentModal invoice={showPayment} onClose={() => setShowPayment(null)} onDone={() => { setShowPayment(null); load(); }} />}
    </div>
  );
}

// ── Fizetés rögzítés modal ──

function PaymentModal({ invoice, onClose, onDone }: { invoice: OpenInvoice; onClose: () => void; onDone: () => void }) {
  const { toast } = useToast();
  const remaining = (invoice.subtotal_gross - invoice.paid_amount) / 100;
  const [amount, setAmount] = useState(remaining);
  const [method, setMethod] = useState(invoice.payment_method || 'transfer');
  const [saving, setSaving] = useState(false);

  async function handleSave() {
    if (amount <= 0) { toast('Az összeg pozitív kell legyen.', 'error'); return; }
    setSaving(true);
    try {
      await post(`/invoices/${invoice.id}/payments`, { amount, paidAt: new Date().toISOString().slice(0, 10), method });
      toast('Fizetés rögzítve.', 'success');
      onDone();
    } catch (e: any) { toast(e.message || 'Hiba.', 'error'); }
    finally { setSaving(false); }
  }

  return (
    <Modal isOpen={true} onClose={onClose} title="Fizetés rögzítése">
      <div className="space-y-4">
        <div style={{ padding: '10px 14px', background: 'var(--mist)', borderRadius: 'var(--r)', fontSize: '13px' }}>
          <div><b>Számla:</b> {invoice.invoice_number?.toUpperCase()}</div>
          <div><b>Partner:</b> {invoice.partner_name}</div>
          <div><b>Hátralék:</b> <span style={{ fontWeight: 700, color: 'var(--red)' }}>{fmt(remaining)}</span></div>
        </div>
        <MoneyInput label="Fizetett összeg (Ft) *" value={amount} onChange={setAmount} />
        <div>
          <label className="form-label">Fizetési mód</label>
          <select value={method} onChange={e => setMethod(e.target.value)} className="form-input">
            <option value="transfer">Átutalás</option>
            <option value="cash">Készpénz</option>
            <option value="card">Bankkártya</option>
          </select>
        </div>
      </div>
      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px', paddingTop: '14px', borderTop: '1px solid var(--line)', marginTop: '14px' }}>
        <Button variant="ghost" onClick={onClose}>Mégse</Button>
        <Button onClick={handleSave} loading={saving}>Fizetés rögzítése</Button>
      </div>
    </Modal>
  );
}
